import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import type { ManagerDashboardContextValue } from '../interfaces'
import { useLatestProjects } from '../hooks/useLatestProjects'
import { useLatestClasses } from '../hooks/useLatestClasses'

const ManagerDashboardContext = createContext<ManagerDashboardContextValue | undefined>(undefined)

/** Owns the dashboard data so sections read from context instead of prop drilling. */
export const ManagerDashboardProvider = ({ children }: { children: ReactNode }) => {
  const { projects, isLoading: projectsLoading, isError: projectsError } = useLatestProjects()
  const { classes, isLoading: classesLoading, isError: classesError } = useLatestClasses()

  const value = useMemo<ManagerDashboardContextValue>(
    () => ({ projects, projectsLoading, projectsError, classes, classesLoading, classesError }),
    [projects, projectsLoading, projectsError, classes, classesLoading, classesError],
  )

  return <ManagerDashboardContext.Provider value={value}>{children}</ManagerDashboardContext.Provider>
}

export const useManagerDashboard = (): ManagerDashboardContextValue => {
  const context = useContext(ManagerDashboardContext)
  if (!context) {
    throw new Error('useManagerDashboard must be used within ManagerDashboardProvider')
  }
  return context
}
